import * as React from "react";

import { set } from "@impero-com/common/utils/obj";
import { KeyTrap } from "@impero-com/ui";

import { copyDataToClipboard } from "utils/clipboard";

import {
    Column,
    CellKeyboardEventType,
    CellKeyboardEvent,
    RawSelectionDescriptor,
    SortedRow,
    SortedSelectionDescriptor,
} from "./types";

/**
 * Selection descriptor. It may evolve over time.
 */
interface Selection {
    colNames: Array<string>;
    rowNumbers: Array<number>;
}

/**
 * Selection handlers may export current selection.
 */
interface SelectionHandlerLike {
    getSelection(): Selection | null;
}

/**
 * The selection handler is a high-level component. It doesn't render anything
 * itself, but only adds props to its children.
 *
 * This implementation is the selection handler used for editable tables. It tracks what cell is currently selected, if any, and whether the user is in 'focus' or 'edit' mode.
 */
class StandardSelectionHandler<Row>
    extends React.Component<
        SelectionHandlerProps<Row>,
        StandardSelectionHandlerState
    >
    implements SelectionHandlerLike
{
    private lastMovementTime: number | null;
    constructor(props: SelectionHandlerProps<Row>) {
        super(props);
        this.lastMovementTime = null;
        this.state = {
            selection: undefined,
            isShiftDown: false,
        };
    }
    shouldComponentUpdate(
        nextProps: SelectionHandlerProps<Row>,
        nextState: StandardSelectionHandlerState
    ) {
        return (
            this.props.columns !== nextProps.columns ||
            this.props.rows !== nextProps.rows ||
            this.state.selection !== nextState.selection
        );
    }
    componentWillReceiveProps(newProps: SelectionHandlerProps<Row>) {
        // XXX this code doesn't really work in SELECT mode, but this is not a regression
        const oldSelection = this.state.selection;
        let newRowNumber = -1;
        let newColumnName: string | null = null;
        if (newProps.activeRow !== undefined && newProps.activeRow > 0) {
            // The caller wants an active row, try to use it
            newRowNumber = newProps.activeRow;
        } else if (oldSelection) {
            // Otherwise make sure the old row number is still inside the boundaries
            const maxRowNumber = this.getMaxRowNumber(
                newProps,
                oldSelection.type
            );
            newRowNumber = Math.min(
                oldSelection.row.sortedRowNumber,
                maxRowNumber
            );
        }
        if (oldSelection) {
            // Try to use the old active column name in any case
            newColumnName = oldSelection.columnName;
        }
        // If we have a row number but no column name then use the first column if it exists
        if (
            newColumnName === null &&
            newRowNumber >= 0 &&
            newProps.columns.length
        ) {
            newColumnName = newProps.columns[0].name;
        }
        if (newRowNumber < 0 || newColumnName === null) {
            if (oldSelection) {
                // Cannot have a selection, reset
                this.doSetState({ selection: undefined });
            }
        } else if (
            !oldSelection ||
            newRowNumber !== oldSelection.row.sortedRowNumber ||
            newColumnName !== oldSelection.columnName
        ) {
            // Selection changed, set the state accordingly and it will call onActiveCellChanged
            const newSelection: SortedSelectionDescriptor = oldSelection
                ? set(oldSelection, {
                      row: { sortedRowNumber: newRowNumber },
                      columnName: newColumnName,
                  })
                : {
                      type: "FOCUS",
                      row: { sortedRowNumber: newRowNumber },
                      columnName: newColumnName,
                  };
            this.doSetState({ selection: newSelection });
        }
    }

    render() {
        const originalGetCellClassName = this.props.getCellClassName;
        const getCellClassName = originalGetCellClassName
            ? (colName: string, rowNumber: number) => {
                  const classNames = [
                      originalGetCellClassName(colName, rowNumber),
                      this.getCellClassName(colName, rowNumber),
                  ];
                  return classNames.filter((className) => className).join(" ");
              }
            : (colName: string, rowNumber: number) =>
                  this.getCellClassName(colName, rowNumber);
        let childSelection: RawSelectionDescriptor | undefined;
        const { selection } = this.state;
        if (selection) {
            switch (selection.type) {
                case "FOCUS":
                    childSelection = {
                        type: selection.type,
                        row: {
                            rawRowNumber: this.toActualRowNumber(
                                selection.row.sortedRowNumber
                            ),
                        },
                        columnName: selection.columnName,
                    };
                    break;
                case "EDIT":
                    childSelection = {
                        type: selection.type,
                        row: {
                            rawRowNumber: this.toActualRowNumber(
                                selection.row.sortedRowNumber
                            ),
                        },
                        columnName: selection.columnName,
                    };
                    break;
                case "SELECT":
                    childSelection = {
                        type: selection.type,
                        row: {
                            rawRowNumber: this.toActualRowNumber(
                                selection.row.sortedRowNumber
                            ),
                        },
                        columnName: selection.columnName,
                        anchorRow: {
                            rawRowNumber: this.toActualRowNumber(
                                selection.anchorRow.sortedRowNumber
                            ),
                        },
                        anchorColumnName: selection.anchorColumnName,
                    };
                    break;
            }
        }
        return (
            <KeyTrap
                onLocalKeyUp={this.handleLocalKeyUp()}
                onLocalKeyDown={this.handleLocalKeyDown()}
            >
                <WrapChild
                    getCellClassName={getCellClassName}
                    selection={childSelection}
                    onCellClick={(
                        colName: string,
                        rowNumber: number,
                        isEditable: () => boolean,
                        keepFocus: () => void
                    ) =>
                        this.handleCellClick(
                            colName,
                            rowNumber,
                            isEditable,
                            keepFocus
                        )
                    }
                    onCellPaste={(
                        colName: string,
                        rowNumber: number,
                        clipboardData: DataTransfer
                    ) => this.handlePaste(colName, rowNumber, clipboardData)}
                    onHotkey={this.handleCellHotkey}
                    onBlur={(colName: string, rowNumber: number) =>
                        this.handleBlur(colName, rowNumber)
                    }
                    onRowSelected={(rowNumber: number) =>
                        this.handleRowSelected(rowNumber)
                    }
                >
                    {this.props.children}
                </WrapChild>
            </KeyTrap>
        );
    }

    getSelection(): Selection | null {
        const { selection } = this.state;
        if (!selection) {
            return null;
        }
        const [leftmostColIndex, rightmostColIndex] =
            this.getSelectionColRange(selection);
        const [topRowNumber, bottomRowNumber] =
            this.getSelectionRowRange(selection);
        const colNames = this.props.columns
            .filter(
                (col, index) =>
                    leftmostColIndex <= index && index <= rightmostColIndex
            )
            .map((col) => col.name);
        const rowNumbers: Array<number> = [];
        for (let i = topRowNumber; i <= bottomRowNumber; i++) {
            // Don't try to copy the empty row
            if (i !== this.props.rows.length) {
                rowNumbers.push(this.toActualRowNumber(i));
            }
        }
        return { colNames, rowNumbers };
    }

    private handleLocalKeyUp() {
        return {
            shift: () => {
                if (this.state.isShiftDown) {
                    this.setState({ isShiftDown: false });
                }
                return true;
            },
        };
    }
    private handleLocalKeyDown() {
        return {
            shift: () => {
                if (!this.state.isShiftDown) {
                    this.setState({ isShiftDown: true });
                }
                return true;
            },
        };
    }

    private handleCellClick(
        colName: string,
        actualRowNumber: number,
        isEditable: () => boolean,
        keepFocus: () => void
    ): void {
        const sortedRowNumber = this.toSortedRowNumber(actualRowNumber);
        let newSelection: SortedSelectionDescriptor | undefined;
        if (
            this.state.selection &&
            colName === this.state.selection.columnName &&
            sortedRowNumber === this.state.selection.row.sortedRowNumber
        ) {
            if (isEditable()) {
                newSelection = {
                    type: "EDIT",
                    row: { sortedRowNumber },
                    columnName: colName,
                };
            } else {
                keepFocus();
            }
        } else if (this.state.isShiftDown) {
            if (!this.state.selection) {
                // no anchor
                // active cell = clicked cell
                newSelection = {
                    type: "FOCUS",
                    row: { sortedRowNumber },
                    columnName: colName,
                };
            } else if (this.state.selection.type !== "SELECT") {
                // anchor = old active cell,
                // active cell = clicked cell
                newSelection = {
                    type: "SELECT",
                    row: { sortedRowNumber },
                    columnName: colName,
                    anchorRow: this.state.selection.row,
                    anchorColumnName: this.state.selection.columnName,
                };
            } else {
                // anchor unchanged
                // active cell = clicked cell
                newSelection = {
                    type: this.state.selection.type,
                    row: { sortedRowNumber },
                    columnName: colName,
                    anchorRow: this.state.selection.anchorRow,
                    anchorColumnName: this.state.selection.anchorColumnName,
                };
            }
        } else {
            // active cell = clicked cell
            newSelection = {
                type: "FOCUS",
                row: { sortedRowNumber },
                columnName: colName,
            };
        }
        if (newSelection) {
            this.doSetState({ selection: newSelection });
        }
    }

    private handleBlur(colName: string, actualRowNumber: number): void {
        const sortedRowNumber = this.toSortedRowNumber(actualRowNumber);
        // see handlePasteInputBlur() in TCell for explanation
        if (
            this.state.selection &&
            colName === this.state.selection.columnName &&
            sortedRowNumber === this.state.selection.row.sortedRowNumber
        ) {
            this.doSetState({ selection: undefined, isShiftDown: false });
        }
    }

    private handlePaste(
        colName: string,
        rowNumber: number,
        clipboardData: DataTransfer
    ): void {
        const { selection } = this.state;
        let pasteAnchorColName: string, pasteAnchorRowNumber: number;
        if (typeof this.props.onCellPaste !== "function") {
            return;
        }
        if (selection && selection.type === "FOCUS") {
            pasteAnchorColName = colName;
            pasteAnchorRowNumber = rowNumber;
        } else if (selection && selection.type === "SELECT") {
            const leftmostSelectionColIndex =
                this.getSelectionColRange(selection)[0];
            pasteAnchorColName =
                this.props.columns[leftmostSelectionColIndex].name;
            pasteAnchorRowNumber = this.getSelectionRowRange(selection)[0];
        } else {
            if (window.console) {
                console.warn(
                    "The selection handler got a paste event while in mode: ",
                    selection,
                    ", but does not know how to handle it"
                );
            }
            return;
        }
        this.props.onCellPaste(
            pasteAnchorColName,
            pasteAnchorRowNumber,
            clipboardData
        );
    }

    private handleRowSelected(actualRowNumber: number): void {
        const { columns } = this.props;
        const { selection, isShiftDown } = this.state;
        const sortedRowNumber = this.toSortedRowNumber(actualRowNumber);
        const colName = columns[columns.length - 1].name;
        const newSelection: SortedSelectionDescriptor =
            selection && selection.type === "SELECT" && isShiftDown
                ? {
                      type: "SELECT",
                      row: { sortedRowNumber },
                      columnName: colName,
                      anchorRow: selection.anchorRow,
                      anchorColumnName: selection.anchorColumnName,
                  }
                : {
                      type: "SELECT",
                      row: { sortedRowNumber },
                      columnName: colName,
                      anchorRow: { sortedRowNumber },
                      anchorColumnName: columns[0].name,
                  };
        this.doSetState({ selection: newSelection });
    }

    private handleCellHotkey = (
        e: CellKeyboardEvent,
        isEditable: () => boolean
    ): void => {
        const { columns } = this.props;
        const { selection } = this.state;
        let newSelection: SortedSelectionDescriptor | undefined;
        const MIN_TIME_BETWEEN_MOVEMENTS_IN_MS = 300;
        const now = new Date().getTime();
        const movementAuthorized =
            this.lastMovementTime !== null
                ? now - this.lastMovementTime > MIN_TIME_BETWEEN_MOVEMENTS_IN_MS
                : true;
        if (
            movementAuthorized &&
            (e.type === CellKeyboardEventType.MOVEMENT ||
                e.type === CellKeyboardEventType.SELECTION_MOVEMENT) &&
            selection
        ) {
            this.lastMovementTime = now;
            const modeIfCanMoveFocus =
                e.type === CellKeyboardEventType.SELECTION_MOVEMENT
                    ? "SELECT"
                    : "FOCUS";
            let maybeNewRow: SortedRow | null = null;
            let maybeNewColName: string | null = null;
            if (e.key === "ArrowUp" || /*#iloveie*/ e.key === "Up") {
                maybeNewRow = this.getMaybeNewRow(
                    selection,
                    -1,
                    modeIfCanMoveFocus
                );
                maybeNewColName = selection.columnName;
            } else if (e.key === "ArrowDown" || /*#iloveie*/ e.key === "Down") {
                maybeNewRow = this.getMaybeNewRow(
                    selection,
                    1,
                    modeIfCanMoveFocus
                );
                maybeNewColName = selection.columnName;
            } else if (e.key === "ArrowLeft" || /*#iloveie*/ e.key === "Left") {
                maybeNewRow = selection.row;
                maybeNewColName = this.getMaybeNewColName(selection, -1);
            } else if (
                e.key === "ArrowRight" ||
                /*#iloveie*/ e.key === "Right"
            ) {
                maybeNewRow = selection.row;
                maybeNewColName = this.getMaybeNewColName(selection, 1);
            }
            if (maybeNewRow !== null && maybeNewColName !== null) {
                if (e.type === CellKeyboardEventType.SELECTION_MOVEMENT) {
                    newSelection = {
                        type: "SELECT",
                        row: maybeNewRow,
                        columnName: maybeNewColName,
                        anchorRow:
                            selection.type === "SELECT"
                                ? selection.anchorRow
                                : selection.row,
                        anchorColumnName:
                            selection.type === "SELECT"
                                ? selection.anchorColumnName
                                : selection.columnName,
                    };
                } else {
                    newSelection = {
                        type: "FOCUS",
                        row: maybeNewRow,
                        columnName: maybeNewColName,
                    };
                }
            }
        } else if (
            movementAuthorized &&
            (e.type === CellKeyboardEventType.NEXT_CELL ||
                e.type === CellKeyboardEventType.PREVIOUS_CELL ||
                e.type === CellKeyboardEventType.END_EDITION_AND_NEXT_CELL ||
                e.type ===
                    CellKeyboardEventType.END_EDITION_AND_PREVIOUS_CELL) &&
            selection
        ) {
            this.lastMovementTime = now;
            const shiftCellOffset =
                e.type === CellKeyboardEventType.NEXT_CELL ||
                e.type === CellKeyboardEventType.END_EDITION_AND_NEXT_CELL
                    ? 1
                    : -1;
            let maybeNewColName = this.getMaybeNewColName(
                selection,
                shiftCellOffset
            );
            let maybeNewRow: SortedRow | null;
            // If we failed to move forward/backward col-wise, try to move row-wise
            if (maybeNewColName === null) {
                maybeNewRow = this.getMaybeNewRow(
                    selection,
                    shiftCellOffset,
                    "FOCUS"
                );
                // If we managed to move row-wise, switch to the first or last column
                if (maybeNewRow !== null) {
                    maybeNewColName =
                        e.type === CellKeyboardEventType.NEXT_CELL
                            ? columns[0].name
                            : columns[columns.length - 1].name;
                }
            } else {
                maybeNewRow = selection.row;
            }
            if (maybeNewRow !== null && maybeNewColName !== null) {
                newSelection = {
                    type: "FOCUS",
                    row: maybeNewRow,
                    columnName: maybeNewColName,
                };
            }
        } else if (e.type === CellKeyboardEventType.START_EDITION) {
            if (isEditable() && selection) {
                newSelection = {
                    type: "EDIT",
                    row: selection.row,
                    columnName: selection.columnName,
                };
            }
        } else if (e.type === CellKeyboardEventType.END_EDITION) {
            if (selection) {
                newSelection = {
                    type: "FOCUS",
                    row: selection.row,
                    columnName: selection.columnName,
                };
            }
        } else if (
            e.type === CellKeyboardEventType.DELETION &&
            selection &&
            selection.type === "SELECT"
        ) {
            newSelection = {
                type: "FOCUS",
                row: selection.row,
                columnName: selection.columnName,
            };
            if (this.props.onDeleteCells) {
                const [selectionStartColIndex, selectionEndColIndex] =
                    this.getSelectionColRange(selection);
                const [selectionStartRowNumber, selectionEndRowNumber] =
                    this.getSelectionRowRange(selection);
                const deletedColNames = this.props.columns
                    .map((col, index) => {
                        const colAndIndex: [
                            Column<Row, any, any, any>,
                            number,
                        ] = [col, index];
                        return colAndIndex;
                    })
                    .filter(
                        (colAndIndex) =>
                            selectionStartColIndex <= colAndIndex[1] &&
                            colAndIndex[1] <= selectionEndColIndex
                    )
                    .map(
                        (colAndIndex: [Column<Row, any, any, any>, number]) =>
                            colAndIndex[0].name
                    );
                const deletedRowNumbers: Array<number> = [];
                for (
                    let i = selectionStartRowNumber;
                    i <= selectionEndRowNumber;
                    i++
                ) {
                    deletedRowNumbers.push(this.toActualRowNumber(i));
                }

                this.props.onDeleteCells(deletedColNames, deletedRowNumbers);
            }
        } else if (e.type === CellKeyboardEventType.COPY) {
            if (
                !selection ||
                (selection.type !== "FOCUS" && selection.type !== "SELECT") ||
                !this.props.getCopyable
            ) {
                return;
            }
            const sel = this.getSelection();
            if (sel) {
                const copyable = this.props.getCopyable(
                    sel.colNames,
                    sel.rowNumbers
                );
                if (copyable !== null) {
                    copyDataToClipboard(copyable);
                }
            }
        } else if (e.type === CellKeyboardEventType.SELECT_ALL) {
            if (this.props.rows.length > 0 && this.props.columns.length > 0) {
                newSelection = {
                    type: "SELECT",
                    row: { sortedRowNumber: this.props.rows.length - 1 },
                    columnName:
                        this.props.columns[this.props.columns.length - 1].name,
                    anchorRow: { sortedRowNumber: 0 },
                    anchorColumnName: this.props.columns[0].name,
                };
            }
        }
        if (newSelection) {
            this.doSetState({ selection: newSelection });
        }
    };
    private getMaybeNewRow(
        selection: SortedSelectionDescriptor,
        offset: number,
        newMode: "FOCUS" | "EDIT" | "SELECT"
    ): SortedRow | null {
        const newRowNumber = selection.row.sortedRowNumber + offset;
        if (
            0 <= newRowNumber &&
            newRowNumber <= this.getMaxRowNumber(this.props, newMode)
        ) {
            return { sortedRowNumber: newRowNumber };
        } else {
            return null;
        }
    }
    private getMaybeNewColName(
        selection: SortedSelectionDescriptor,
        offset: number
    ): string | null {
        const colName = selection.columnName;
        const colNumber = this.getColIndex(colName);
        const newColNumber = colNumber + offset;
        if (
            this.props.columns.length > 0 &&
            0 <= newColNumber &&
            newColNumber < this.props.columns.length
        ) {
            return this.props.columns[newColNumber].name;
        } else {
            return null;
        }
    }

    private getCellClassName(
        colName: string,
        actualRowNumber: number
    ): string | undefined {
        const { styles } = this.props;
        const sortedRowNumber = this.toSortedRowNumber(actualRowNumber);
        const classNames: Array<string> = [];
        if (this.isCellFocused(colName, sortedRowNumber)) {
            classNames.push(styles.activeCellClassName);
        }
        if (this.isCellEdited(colName, sortedRowNumber)) {
            classNames.push(styles.editedCellClassName);
        }
        if (this.isCellAnchor(colName, sortedRowNumber)) {
            classNames.push(styles.anchorCellClassName);
        }
        if (this.isCellSelected(colName, sortedRowNumber)) {
            classNames.push(styles.selectedCellClassName);
            const [selectionStartColIndex, selectionEndColIndex] =
                this.getSelectionColRange(this.state.selection! /*isSelected*/);
            const colIndex = this.getColIndex(colName);
            if (selectionStartColIndex === colIndex) {
                classNames.push(styles.leftSelectionClassName);
            }
            if (selectionEndColIndex === colIndex) {
                classNames.push(styles.rightSelectionClassName);
            }
            const [selectionStartRowNumber, selectionEndRowNumber] =
                this.getSelectionRowRange(this.state.selection! /*isSelected*/);
            if (selectionStartRowNumber === sortedRowNumber) {
                classNames.push(styles.topSelectionClassName);
            }
            if (selectionEndRowNumber === sortedRowNumber) {
                classNames.push(styles.bottomSelectionClassName);
            }
        }
        return classNames.length > 0 ? classNames.join(" ") : undefined;
    }

    private isCellFocused(colName: string, sortedRowNumber: number): boolean {
        const { selection } = this.state;
        return (
            !!selection &&
            colName === selection.columnName &&
            sortedRowNumber === selection.row.sortedRowNumber
        );
    }

    private isCellAnchor(colName: string, sortedRowNumber: number): boolean {
        const { selection } = this.state;
        return (
            !!selection &&
            selection.type === "SELECT" &&
            colName === selection.anchorColumnName &&
            sortedRowNumber === selection.anchorRow.sortedRowNumber
        );
    }

    private isCellSelected(colName: string, sortedRowNumber: number): boolean {
        const { selection } = this.state;
        // FIXME If we don't test SELECT then it breaks the empty row paste input
        if (!selection || selection.type !== "SELECT") {
            return false;
        }
        const [selectionStartRowNumber, selectionEndRowNumber] =
            this.getSelectionRowRange(selection);
        if (
            sortedRowNumber < selectionStartRowNumber ||
            sortedRowNumber > selectionEndRowNumber
        ) {
            return false;
        }
        const [selectionStartColIndex, selectionEndColIndex] =
            this.getSelectionColRange(selection);
        const colIndex = this.getColIndex(colName);
        return (
            selectionStartColIndex <= colIndex &&
            colIndex <= selectionEndColIndex
        );
    }

    private getSelectionRowRange(
        selection: SortedSelectionDescriptor
    ): [number, number] {
        const rowNumbers = [selection.row.sortedRowNumber];
        if (selection.type === "SELECT") {
            rowNumbers.push(selection.anchorRow.sortedRowNumber);
        } else {
            rowNumbers.push(rowNumbers[0]);
        }
        rowNumbers.sort((a, b) => {
            if (a < b) {
                return -1;
            } else if (a > b) {
                return 1;
            } else {
                return 0;
            }
        });
        return [rowNumbers[0], rowNumbers[1]];
    }
    private getSelectionColRange(
        selection: SortedSelectionDescriptor
    ): [number, number] {
        const indexes = [this.getColIndex(selection.columnName)];
        if (selection.type === "SELECT") {
            indexes.push(this.getColIndex(selection.anchorColumnName));
        } else {
            indexes.push(indexes[0]);
        }
        indexes.sort((a, b) => {
            if (a < b) {
                return -1;
            } else if (a > b) {
                return 1;
            } else {
                return 0;
            }
        });
        return [indexes[0], indexes[1]];
    }
    private getColIndex(colName: string): number {
        return this.props.columns.findIndex((col) => col.name === colName);
    }
    private isCellEdited(colName: string, sortedRowNumber: number): boolean {
        const { selection } = this.state;
        return (
            !!selection &&
            selection.type === "EDIT" &&
            colName === selection.columnName &&
            sortedRowNumber === selection.row.sortedRowNumber
        );
    }

    private getMaxRowNumber(
        props: SelectionHandlerProps<Row>,
        newMode: "FOCUS" | "EDIT" | "SELECT"
    ): number {
        return props.canAddRow && newMode !== "SELECT"
            ? props.rows.length
            : props.rows.length - 1;
    }

    private doSetState(newState: StandardSelectionHandlerState): void {
        const oldColumnName = this.state.selection
            ? this.state.selection.columnName
            : undefined;
        const newColumnName = newState.selection
            ? newState.selection.columnName
            : undefined;
        const columnNameChanged = newColumnName !== oldColumnName;

        const oldRowNumber = this.state.selection
            ? this.state.selection.row.sortedRowNumber
            : undefined;
        const newRowNumber = newState.selection
            ? newState.selection.row.sortedRowNumber
            : undefined;
        const rowNumberChanged = newRowNumber !== oldRowNumber;

        this.setState(newState);
        if (
            this.props.onActiveCellChanged &&
            (columnNameChanged || rowNumberChanged)
        ) {
            // TODO change the prototype of onActiveCellChanged
            this.props.onActiveCellChanged(
                newColumnName !== undefined ? newColumnName : "",
                newRowNumber !== undefined
                    ? this.toActualRowNumber(newRowNumber)
                    : -1
            );
        }
    }

    private toSortedRowNumber(actualRowNumber: number): number {
        return actualRowNumber !== -1 &&
            // That's the position of the final empty row, should always
            // be at the end
            actualRowNumber !== this.props.rows.length &&
            this.props.actualRowNumberToSortedRowNumber
            ? this.props.actualRowNumberToSortedRowNumber[actualRowNumber]
            : actualRowNumber;
    }

    private toActualRowNumber(sortedRowNumber: number): number {
        return sortedRowNumber !== -1 &&
            // That's the position of the final empty row, should always
            // be at the end
            sortedRowNumber !== this.props.rows.length &&
            this.props.sortedRowNumberToActualRowNumber
            ? this.props.sortedRowNumberToActualRowNumber[sortedRowNumber]
            : sortedRowNumber;
    }
}

interface StandardSelectionHandlerState {
    selection?: SortedSelectionDescriptor;
    isShiftDown?: boolean;
}

/**
 * A selection handler for tables which are editable but not selectable.
 *
 * @see StandardSelectionHandler
 */
class EditCellSelectionHandler<Row>
    extends React.Component<
        SelectionHandlerProps<Row>,
        EditCellSelectionHandlerState
    >
    implements SelectionHandlerLike
{
    constructor(props: SelectionHandlerProps<Row>) {
        super(props);
        this.state = {
            selection: undefined,
        };
    }
    render() {
        const getNull: () => any = () => null;
        const noop = () => {};
        const getCellClassName =
            typeof this.props.getCellClassName === "function"
                ? this.props.getCellClassName
                : getNull;
        let childSelection: RawSelectionDescriptor | undefined;
        const { selection } = this.state;
        if (selection) {
            switch (selection.type) {
                case "EDIT":
                    childSelection = {
                        type: selection.type,
                        row: {
                            rawRowNumber: this.toActualRowNumber(
                                selection.row.sortedRowNumber
                            ),
                        },
                        columnName: selection.columnName,
                    };
                    break;
            }
        }
        return (
            <WrapChild
                getCellClassName={getCellClassName}
                selection={childSelection}
                onCellClick={(
                    colName: string,
                    rowNumber: number,
                    isEditable: () => boolean,
                    keepFocus: () => void
                ) =>
                    this.handleCellClick(
                        colName,
                        rowNumber,
                        isEditable,
                        keepFocus
                    )
                }
                onHotkey={this.handleCellHotkey}
                onBlur={noop}
            >
                {this.props.children}
            </WrapChild>
        );
    }
    getSelection(): Selection | null {
        return null;
    }
    private handleCellClick(
        colName: string,
        actualRowNumber: number,
        isEditable: () => boolean,
        keepFocus: () => void
    ): void {
        const sortedRowNumber = this.toSortedRowNumber(actualRowNumber);
        let newSelection: SortedSelectionDescriptor | undefined;
        if (isEditable()) {
            newSelection = {
                type: "EDIT",
                row: { sortedRowNumber },
                columnName: colName,
            };
        } else {
            keepFocus();
        }
        if (newSelection) {
            this.doSetState({ selection: newSelection });
        }
    }
    private handleCellHotkey = (e: CellKeyboardEvent): void => {
        if (e.type === CellKeyboardEventType.END_EDITION) {
            this.doSetState({ selection: undefined });
        }
    };

    private doSetState(newState: EditCellSelectionHandlerState): void {
        const oldColumnName = this.state.selection
            ? this.state.selection.columnName
            : undefined;
        const newColumnName = newState.selection
            ? newState.selection.columnName
            : undefined;
        const columnNameChanged = newColumnName !== oldColumnName;

        const oldRowNumber = this.state.selection
            ? this.state.selection.row.sortedRowNumber
            : undefined;
        const newRowNumber = newState.selection
            ? newState.selection.row.sortedRowNumber
            : undefined;
        const rowNumberChanged = newRowNumber !== oldRowNumber;

        this.setState(newState);
        if (
            this.props.onActiveCellChanged &&
            (columnNameChanged || rowNumberChanged)
        ) {
            // TODO change the prototype of onActiveCellChanged
            this.props.onActiveCellChanged(
                newColumnName !== undefined ? newColumnName : "",
                newRowNumber !== undefined
                    ? this.toActualRowNumber(newRowNumber)
                    : -1
            );
        }
    }

    private toSortedRowNumber(actualRowNumber: number): number {
        return actualRowNumber !== -1 &&
            // That's the position of the final empty row, should always
            // be at the end
            actualRowNumber !== this.props.rows.length &&
            this.props.actualRowNumberToSortedRowNumber
            ? this.props.actualRowNumberToSortedRowNumber[actualRowNumber]
            : actualRowNumber;
    }

    private toActualRowNumber(sortedRowNumber: number): number {
        return sortedRowNumber !== -1 &&
            // That's the position of the final empty row, should always
            // be at the end
            sortedRowNumber !== this.props.rows.length &&
            this.props.sortedRowNumberToActualRowNumber
            ? this.props.sortedRowNumberToActualRowNumber[sortedRowNumber]
            : sortedRowNumber;
    }
}

// Mark everything as optional due to a Typescript limitation
interface EditCellSelectionHandlerState {
    selection?: SortedSelectionDescriptor;
}

/**
 * A selection handler for tables which are not editable. Injects dummy props.
 *
 * @see StandardSelectionHandler
 */
class NoopSelectionHandler<Row>
    extends React.Component<SelectionHandlerProps<Row>>
    implements SelectionHandlerLike
{
    render() {
        const getCellClassName =
            typeof this.props.getCellClassName === "function"
                ? this.props.getCellClassName
                : undefined;
        return (
            <WrapChild getCellClassName={getCellClassName}>
                {this.props.children}
            </WrapChild>
        );
    }
    getSelection(): Selection | null {
        return null;
    }
}

interface SelectionHandlerStyles {
    activeCellClassName: string;
    editedCellClassName: string;
    selectedCellClassName: string;
    topSelectionClassName: string;
    bottomSelectionClassName: string;
    leftSelectionClassName: string;
    rightSelectionClassName: string;
    anchorCellClassName: string;
}

interface SelectionHandlerProps<Row> {
    columns: Array<Column<Row, any, any, any>>;
    rows: Array<Row>;
    children?: React.ReactNode;
    styles: SelectionHandlerStyles;
    onActiveCellChanged?: (colName: string, rowNumber: number) => void;
    actualRowNumberToSortedRowNumber: Array<number> | null;
    sortedRowNumberToActualRowNumber: Array<number> | null;
    getCellClassName?: (
        colName: string,
        rowNumber: number
    ) => string | undefined;
    canAddRow?: boolean;
    getCopyable?: (
        colNames: Array<string>,
        rowNumbers: Array<number>
    ) => string | null;
    onDeleteCells?: (
        colNames: Array<string>,
        rowNumbers: Array<number>
    ) => void;
    activeRow?: number;
    onCellPaste?: (
        colName: string,
        rowNumber: number,
        clipboardData: DataTransfer
    ) => void;
}

class WrapChild<
    InputProps extends WrapChildProps,
> extends React.Component<InputProps> {
    render() {
        // clone children with their props, and inject our own props minus 'children'
        const props = this.props;
        const { children: _, ...propsWithoutChildren } = props;
        const cloneWithProps: (
            child: React.ReactElement,
            propsWithoutChildren: any
        ) => React.ReactElement | number | string = (child) =>
            React.cloneElement(child, propsWithoutChildren);
        const childrenWithProps = React.Children.map(props.children, (c) => {
            if (typeof c === "string" || typeof c === "number") {
                throw new Error("Got non-object child");
            } else {
                return cloneWithProps(
                    c as React.ReactElement,
                    propsWithoutChildren
                );
            }
        });
        const childrenArray = React.Children.toArray(childrenWithProps);
        if (childrenArray.length !== 1) {
            throw new Error("Expected exactly 1 child");
        }
        const res = childrenArray[0];
        // Cheat the typechecker
        if (typeof res === "object") {
            return res;
        } else {
            return null;
        }
    }
}

interface WrapChildProps {
    children?: React.ReactNode;
}
export type {
    SelectionHandlerLike,
    SelectionHandlerStyles,
    SelectionHandlerProps,
};
export {
    StandardSelectionHandler,
    EditCellSelectionHandler,
    NoopSelectionHandler,
};
